/**
 * Theme JS entry point.
 *
 * UIkit and the Icons plugin are initialised here.
 * The compiled output (assets/js/main.js) is enqueued by functions.php —
 * never hardcode it in a template.
 */

import UIkit from 'uikit';
import Icons from 'uikit/dist/js/uikit-icons';
import initCounters from './counter.js';
import initFeaturedPostSliders from './featured-post-slider.js';
import initClipboard from './clipboard.js';
import initLucide from './lucide.js';

// Load the Icons plugin so uk-icon and uk-navbar-toggle-icon work.
UIkit.use( Icons );

// Expose UIkit globally so other separately-bundled scripts can access it.
// Webpack bundles UIkit as a private module and does NOT set window.UIkit
// automatically — we do it explicitly here.
window.UIkit = UIkit;

// Font CSS — imported here (not from SCSS) so css-loader can resolve the url()
// paths and webpack copies the WOFF2 files to assets/fonts/. The SCSS pipeline
// has url:false set, which would leave these paths unresolved at runtime.
import '@fontsource-variable/inter/wght.css';
import '@fontsource-variable/space-grotesk/wght.css';

// Rebrand fonts (Arvo + Schibsted Grotesk) — introduced with the new hero
// design. See _variables.scss "Rebrand tokens" for the $brand-font-* vars.
import '@fontsource/arvo/latin-400.css';
import '@fontsource-variable/schibsted-grotesk/wght.css';

// The SCSS entry point is imported here so webpack compiles both in one pass.
import '../scss/main.scss';

// Script is footer-loaded so the DOM is already ready at this point.
initCounters();
initFeaturedPostSliders();
initClipboard();
initLucide();

// Find a Clinic's Leaflet map (leaflet + its init code, ~34KB) is its own
// webpack entry — assets/js/find-a-clinic-map.js — enqueued only on that
// page template (functions.php), not bundled in here for every page.

// =============================================================================
// Theme JavaScript
// Add your own JS below. UIkit is available as a module-level import above,
// or globally as `window.UIkit` (set by UIkit itself).
// =============================================================================

// Example: programmatic notification
// UIkit.notification({ message: 'Site loaded', status: 'primary' });

(function () {
  // If the browser supports scroll-driven animations, do nothing — CSS handles it.
  if (CSS.supports('animation-timeline', 'view()')) return;

  // ── JS fallback for Firefox and other non-supporting browsers ──
  // Add transition class now that we know CSS animation won't run
  const headings = document.querySelectorAll('.heading-highlight');
  headings.forEach(el => el.classList.add('js-animate'));

  // Use IntersectionObserver to detect when element enters viewport,
  // then use scroll position to drive background-size proportionally.
  const observers = new Map();

  function trackScroll(el) {
    function onScroll() {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;

      // progress: 0 when bottom of element hits bottom of viewport,
      //           1 when element is 60% up the viewport
      const start = vh;           // element enters at bottom
      const end   = vh * 0.4;     // fully revealed when top is 40% from bottom
      const pos   = rect.bottom;

      const progress = Math.min(1, Math.max(0, (start - pos) / (start - end)));

      el.style.backgroundSize = (progress * 100) + '% 100%';

      if (progress >= 1) {
        window.removeEventListener('scroll', onScroll, { passive: true });
        observers.get(el)?.disconnect();
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    observers.set(el, null); // placeholder so we can clean up

    // Run once immediately in case already in view
    onScroll();
  }

  // Only start tracking scroll when element is near the viewport
  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        trackScroll(entry.target);
        io.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  headings.forEach(el => io.observe(el));
})();

// =============================================================================
// Animated background size on .heading-highlight elements as they scroll into view.
// Uses CSS animation-timeline where supported, with a JS fallback for other browsers.
// The JS fallback is not pixel-perfect but provides a similar effect without
// causing jank by running on every scroll frame.
// =============================================================================

(function () {
  // If the browser supports scroll-driven animations, do nothing — CSS handles it.
  if (CSS.supports('animation-timeline', 'view()')) return;

  // ── JS fallback for Firefox and other non-supporting browsers ──
  // Add transition class now that we know CSS animation won't run
  const headings = document.querySelectorAll('.heading-highlight');
  headings.forEach(el => el.classList.add('js-animate'));

  // Use IntersectionObserver to detect when element enters viewport,
  // then use scroll position to drive background-size proportionally.
  const observers = new Map();

  function trackScroll(el) {
    function onScroll() {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;

      // progress: 0 when bottom of element hits bottom of viewport,
      //           1 when element is 60% up the viewport
      const start = vh;           // element enters at bottom
      const end   = vh * 0.4;     // fully revealed when top is 40% from bottom
      const pos   = rect.bottom;

      const progress = Math.min(1, Math.max(0, (start - pos) / (start - end)));

      el.style.backgroundSize = (progress * 100) + '% 100%';

      if (progress >= 1) {
        window.removeEventListener('scroll', onScroll, { passive: true });
        observers.get(el)?.disconnect();
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    observers.set(el, null); // placeholder so we can clean up

    // Run once immediately in case already in view
    onScroll();
  }

  // Only start tracking scroll when element is near the viewport
  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        trackScroll(entry.target);
        io.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  headings.forEach(el => io.observe(el));
})();




// =============================================================================
// Header language switcher — toggles the GTranslate dropdown on icon click,
// and relocates itself next to the mobile hamburger below the medium
// breakpoint (960px, matching UIkit's @m) instead of living in the topbar,
// which is otherwise hidden on mobile. Moves the single DOM node rather
// than duplicating it, so the GTranslate shortcode is never rendered twice.
// =============================================================================

(function () {
    const wrap = document.querySelector( '.site-header-topbar__lang' );
    const toggle = document.querySelector( '.site-header-topbar__lang-toggle' );
    const menu   = document.querySelector( '.site-header-topbar__lang-menu' );
    if ( ! wrap || ! toggle || ! menu ) return;

    const mobileSlot = document.getElementById( 'site-header-lang-mobile-slot' );
    const desktopHome = wrap.parentNode;
    const desktopHomeNextSibling = wrap.nextSibling;
    // Matches $nav-breakpoint (820px) in _variables.scss — 820px itself
    // is still "mobile" there, so this needs to be inclusive too. Keep in sync.
    const mobileQuery = window.matchMedia( '(max-width: 820px)' );

    function place( isMobile ) {
        if ( isMobile && mobileSlot && wrap.parentNode !== mobileSlot ) {
            mobileSlot.appendChild( wrap );
        } else if ( ! isMobile && wrap.parentNode !== desktopHome ) {
            desktopHome.insertBefore( wrap, desktopHomeNextSibling );
        }
    }

    place( mobileQuery.matches );
    mobileQuery.addEventListener( 'change', function ( e ) {
        place( e.matches );
    } );

    function close() {
        menu.hidden = true;
        toggle.setAttribute( 'aria-expanded', 'false' );
    }

    toggle.addEventListener( 'click', function ( e ) {
        e.stopPropagation();
        const isOpen = ! menu.hidden;
        menu.hidden = isOpen;
        toggle.setAttribute( 'aria-expanded', String( ! isOpen ) );
    } );

    document.addEventListener( 'click', function ( e ) {
        if ( ! menu.hidden && ! menu.contains( e.target ) && ! toggle.contains( e.target ) ) {
            close();
        }
    } );

    document.addEventListener( 'keydown', function ( e ) {
        if ( e.key === 'Escape' && ! menu.hidden ) {
            close();
            toggle.focus();
        }
    } );
})();

// =============================================================================
// Book Appointment — visit-type pill tabs scroll into view on click. The
// tabs row scrolls horizontally on mobile (see .book-appointment-visit-types__tabs
// in _page-book-appointment.scss) instead of wrapping; without this, a pill
// clicked near the right edge stays there, hiding whichever pills are past it.
// =============================================================================

(function () {
    const tabs = document.querySelector( '.book-appointment-visit-types__tabs' );
    if ( ! tabs ) return;

    tabs.addEventListener( 'click', function ( e ) {
        const link = e.target.closest( 'a' );
        if ( ! link ) return;

        const li = link.closest( 'li' );
        if ( ! li ) return;

        // No-op once the row isn't actually scrollable (i.e. above the
        // mobile breakpoint where the tabs wrap instead).
        if ( tabs.scrollWidth <= tabs.clientWidth ) return;

        const targetScroll = li.offsetLeft - 16; // small left margin so the pill isn't flush against the edge
        tabs.scrollTo( { left: Math.max( 0, targetScroll ), behavior: 'smooth' } );
    } );
})();

// =============================================================================
// Policies & Forms — category filter pills scroll into view on click. Same
// fix as the Book Appointment visit-type tabs above: the pill row scrolls
// horizontally instead of wrapping on mobile/tablet (see
// .facetwp-facet-form_category in _page-policies-forms.scss), so without
// this a pill clicked near the right edge hides whichever ones are past it.
// Delegated on the outer .facetwp-facet-form_category wrapper (not the pills
// themselves) since FacetWP replaces its inner HTML on every selection.
// =============================================================================

(function () {
    const facet = document.querySelector( '.facetwp-facet-form_category' );
    if ( ! facet ) return;

    facet.addEventListener( 'click', function ( e ) {
        const pill = e.target.closest( '.facetwp-radio' );
        if ( ! pill ) return;

        // No-op once the row isn't actually scrollable (i.e. above the
        // breakpoint where the pills would otherwise wrap).
        if ( facet.scrollWidth <= facet.clientWidth ) return;

        const targetScroll = pill.offsetLeft - 16; // small left margin so the pill isn't flush against the edge
        facet.scrollTo( { left: Math.max( 0, targetScroll ), behavior: 'smooth' } );
    } );
})();

// =============================================================================
// Form cards — clicking anywhere on a [data-form-url] card opens that form
// in a new tab (see .policies-forms-card / .wcv-forms__card and every other
// page's own copy of that same card pattern). The card's own real links
// (Instructions toggle, Download) keep their own behavior — a click
// starting on an <a> is left alone rather than hijacked by this delegate.
// One generic, sitewide delegate rather than one per page's class name.
// =============================================================================

(function () {
    document.querySelectorAll( '[data-form-url]' ).forEach( function ( card ) {
        card.addEventListener( 'click', function ( e ) {
            if ( e.target.closest( 'a' ) ) return;
            window.open( card.dataset.formUrl, '_blank', 'noopener' );
        } );
    } );
})();

// =============================================================================
// Helpful Links & Resources — category filter pills scroll into view on
// click. Same fix as Policies & Forms above (see .facetwp-facet-link_tag in
// _page-helpful-links-resources.scss) — its own facet (link_tag, not
// form_category), so its own delegated listener.
// =============================================================================

(function () {
    const facet = document.querySelector( '.facetwp-facet-link_tag' );
    if ( ! facet ) return;

    facet.addEventListener( 'click', function ( e ) {
        const pill = e.target.closest( '.facetwp-radio' );
        if ( ! pill ) return;

        // No-op once the row isn't actually scrollable (i.e. above the
        // breakpoint where the pills would otherwise wrap).
        if ( facet.scrollWidth <= facet.clientWidth ) return;

        const targetScroll = pill.offsetLeft - 16; // small left margin so the pill isn't flush against the edge
        facet.scrollTo( { left: Math.max( 0, targetScroll ), behavior: 'smooth' } );
    } );
})();

// =============================================================================
// Providers — filter bar scrolls into view on click. Same fix as the two
// facet rows above: the filter row (reset link / "Accepting New Patients"
// checkbox / Locations + Languages dropdowns) scrolls horizontally instead
// of wrapping on mobile/tablet (see .providers-filter__facets in
// _page-providers.scss). Delegated on the row itself and keyed off
// .providers-filter__facet (the wrapper every facet type shares) rather than
// a type-specific class, since this row mixes pill and dropdown facets.
// =============================================================================

(function () {
    const facets = document.querySelector( '.providers-filter__facets' );
    if ( ! facets ) return;

    facets.addEventListener( 'click', function ( e ) {
        const facet = e.target.closest( '.providers-filter__facet' );
        if ( ! facet ) return;

        // No-op once the row isn't actually scrollable (i.e. above the
        // breakpoint where the facets would otherwise wrap).
        if ( facets.scrollWidth <= facets.clientWidth ) return;

        const targetScroll = facet.offsetLeft - 16; // small left margin so the facet isn't flush against the edge
        facets.scrollTo( { left: Math.max( 0, targetScroll ), behavior: 'smooth' } );
    } );
})();

// =============================================================================
// Medical Records — custom always-visible scrollbar for the Q&A list. A
// native scrollbar can't be forced to stay visible at rest on macOS
// regardless of CSS (it's an OS-level overlay-scrollbar setting), so the
// real one is hidden (see .medical-records-qa__list in
// _page-medical-records.scss) and this syncs a fake track+thumb to it instead.
// =============================================================================

(function () {
    const list  = document.querySelector( '.medical-records-qa__list' );
    const track = document.querySelector( '.medical-records-qa__scrollbar' );
    const thumb = document.querySelector( '.medical-records-qa__scrollbar-thumb' );
    if ( ! list || ! track || ! thumb ) return;

    function update() {
        const scrollable = list.scrollHeight > list.clientHeight;
        track.style.display = scrollable ? '' : 'none';
        if ( ! scrollable ) return;

        const trackHeight = track.clientHeight;
        // *0.7 reads closer to the design reference's short, pill-like
        // thumb rather than one spanning the same proportion as the track.
        const thumbHeight = Math.max( 20, ( list.clientHeight / list.scrollHeight ) * trackHeight * 0.7 );
        const maxThumbTop = trackHeight - thumbHeight;
        const scrollRatio = list.scrollTop / ( list.scrollHeight - list.clientHeight );

        thumb.style.height = thumbHeight + 'px';
        thumb.style.top    = ( scrollRatio * maxThumbTop ) + 'px';
    }

    list.addEventListener( 'scroll', update, { passive: true } );
    window.addEventListener( 'resize', update );
    update();
})();

// =============================================================================
// Well-Child Timeline — "Select Age" pills scroll to and open the matching
// accordion row. UIkit's accordion only opens on a real click of its own
// title link, so a pill click finds that title inside the target row and
// clicks it (if not already open) before scrolling the row into view.
// =============================================================================

(function () {
    const pills = document.querySelector( '.wct-timeline__pills' );
    if ( ! pills ) return;

    pills.addEventListener( 'click', function ( e ) {
        const pill = e.target.closest( 'a' );
        if ( ! pill ) return;

        const targetId = pill.getAttribute( 'href' ) || '';
        if ( ! targetId.startsWith( '#' ) ) return;

        const row = document.querySelector( targetId );
        if ( ! row ) return;

        e.preventDefault();

        const title = row.querySelector( '.uk-accordion-title' );
        if ( title && ! row.classList.contains( 'uk-open' ) ) {
            title.click();
        }

        row.scrollIntoView( { behavior: 'smooth', block: 'start' } );
    } );
})();

// =============================================================================
// Slider — click-and-drag on the row itself (a mouse has no horizontal-
// scroll gesture, so drag is how a mouse user moves it; touch/trackpad
// already swipe natively) plus a visible scroll-progress bar so it's
// obvious there's more to see, at a glance — generic by data attribute, not
// tied to any one component's class names, so every horizontally-
// scrollable card row on the site can opt in the same way
// (.helpful-resources, .home-announcements, ...). Markup contract (see
// main.scss):
//   [data-slider]           — wrapper
//   [data-slider-track]     — the actual scrollable row inside it
//   [data-slider-scrollbar] — the progress-bar track
//   [data-slider-thumb]     — the progress-bar thumb inside it
// A page can have several (e.g. Care Coordination's per-condition
// sections) — each [data-slider] gets its own independent instance.
// =============================================================================

(function () {
    const sliders = document.querySelectorAll( '[data-slider]' );
    if ( ! sliders.length ) return;

    sliders.forEach( function ( slider ) {
        const row       = slider.querySelector( '[data-slider-track]' );
        const scrollbar = slider.querySelector( '[data-slider-scrollbar]' );
        const thumb     = slider.querySelector( '[data-slider-thumb]' );
        if ( ! row || ! scrollbar || ! thumb ) return;

        // ── Scroll-progress bar ──────────────────────────────────────────
        // Thumb width reflects how much of the row is visible at once;
        // thumb position reflects how far through the scrollable range the
        // row currently is. Also the thing that makes "there's more"
        // obvious without the user having to try scrolling first to find out.

        const updateThumb = function () {
            const maxScroll = row.scrollWidth - row.clientWidth;

            if ( maxScroll <= 1 ) {
                scrollbar.style.display = 'none';
                return;
            }
            scrollbar.style.display = '';

            const trackWidth = scrollbar.clientWidth;
            const thumbWidthRatio = Math.min( row.clientWidth / row.scrollWidth, 1 );
            const thumbWidth = Math.max( trackWidth * thumbWidthRatio, 40 );
            const maxThumbOffset = trackWidth - thumbWidth;
            const scrollRatio = row.scrollLeft / maxScroll;

            thumb.style.width = thumbWidth + 'px';
            thumb.style.transform = 'translateX(' + ( maxThumbOffset * scrollRatio ) + 'px)';
        };

        row.addEventListener( 'scroll', updateThumb, { passive: true } );
        window.addEventListener( 'resize', updateThumb );
        updateThumb();

        // ── Drag-to-scroll on the row ────────────────────────────────────
        // Mouse only (touch/trackpad already scroll the row natively — a
        // pointer-based handler here would double up with that and fight
        // native momentum scrolling on touch devices).

        let isDragging  = false;
        let dragMoved    = false;
        let dragStartX  = 0;
        let dragStartScroll = 0;

        const startDrag = function ( pageX, startScrollLeft ) {
            isDragging = true;
            dragMoved  = false;
            dragStartX = pageX;
            dragStartScroll = startScrollLeft;
            row.classList.add( 'is-dragging' );
        };

        const endDrag = function () {
            if ( ! isDragging ) return;
            isDragging = false;
            row.classList.remove( 'is-dragging' );
        };

        row.addEventListener( 'mousedown', function ( e ) {
            startDrag( e.pageX, row.scrollLeft );
        } );

        window.addEventListener( 'mousemove', function ( e ) {
            if ( ! isDragging ) return;
            const delta = e.pageX - dragStartX;
            if ( Math.abs( delta ) > 3 ) dragMoved = true;
            row.scrollLeft = dragStartScroll - delta;
        } );

        window.addEventListener( 'mouseup', endDrag );
        row.addEventListener( 'mouseleave', endDrag );

        // A drag that moved the row shouldn't also fire the link/button the
        // mouse happened to release over — swallow just that one click.
        row.addEventListener( 'click', function ( e ) {
            if ( dragMoved ) {
                e.preventDefault();
                e.stopPropagation();
                dragMoved = false;
            }
        }, true );

        // ── Drag the scrollbar thumb directly ────────────────────────────

        let isThumbDragging = false;
        let thumbStartX = 0;
        let thumbStartScroll = 0;

        thumb.addEventListener( 'mousedown', function ( e ) {
            isThumbDragging = true;
            thumbStartX = e.pageX;
            thumbStartScroll = row.scrollLeft;
            thumb.classList.add( 'is-dragging' );
            e.preventDefault(); // don't also start a row-drag under this
        } );

        window.addEventListener( 'mousemove', function ( e ) {
            if ( ! isThumbDragging ) return;
            const maxScroll   = row.scrollWidth - row.clientWidth;
            const trackWidth  = scrollbar.clientWidth - thumb.offsetWidth;
            if ( trackWidth <= 0 ) return;
            const scrollPerPx = maxScroll / trackWidth;
            row.scrollLeft = thumbStartScroll + ( e.pageX - thumbStartX ) * scrollPerPx;
        } );

        window.addEventListener( 'mouseup', function () {
            isThumbDragging = false;
            thumb.classList.remove( 'is-dragging' );
        } );

        // Click elsewhere on the track jumps the thumb (and row) there.
        scrollbar.addEventListener( 'mousedown', function ( e ) {
            if ( e.target === thumb ) return;
            const maxScroll  = row.scrollWidth - row.clientWidth;
            const trackRect  = scrollbar.getBoundingClientRect();
            const clickRatio = ( e.clientX - trackRect.left ) / trackRect.width;
            row.scrollLeft = maxScroll * clickRatio;
        } );
    } );
})();

// =============================================================================
// Slider arrow navigation — optional add-on to the generic [data-slider]
// system above. Desktop/widescreen replacement for the touch-drag +
// scrollbar pattern (see .slider-arrow in main.scss): a [data-slider] only
// gets prev/next buttons if its markup includes them, so this has zero
// effect on sliders that don't (e.g. Helpful Resources, Care Coordination).
// Currently only Home's Announcements row opts in.
//   [data-slider-prev] / [data-slider-next] — buttons, anywhere inside the
//   same [data-slider] wrapper as [data-slider-track].
// Scrolls by one card's width (+ its gap) per click, read directly off the
// first two cards' positions so it stays correct across breakpoints without
// hardcoding a card width here.
// =============================================================================

(function () {
    const sliders = document.querySelectorAll( '[data-slider]' );
    if ( ! sliders.length ) return;

    sliders.forEach( function ( slider ) {
        const row  = slider.querySelector( '[data-slider-track]' );
        const prev = slider.querySelector( '[data-slider-prev]' );
        const next = slider.querySelector( '[data-slider-next]' );
        if ( ! row || ( ! prev && ! next ) ) return;

        const step = function () {
            const cards = row.children;
            if ( cards.length < 2 ) return row.clientWidth;
            // Distance between two consecutive cards' left edges — their
            // width plus the row's gap — however many fit per "page" at the
            // current breakpoint.
            return cards[ 1 ].offsetLeft - cards[ 0 ].offsetLeft;
        };

        const updateDisabled = function () {
            const maxScroll = row.scrollWidth - row.clientWidth;
            if ( prev ) prev.disabled = row.scrollLeft <= 0;
            if ( next ) next.disabled = row.scrollLeft >= maxScroll - 1;
        };

        if ( prev ) {
            prev.addEventListener( 'click', function () {
                row.scrollBy( { left: -step(), behavior: 'smooth' } );
            } );
        }
        if ( next ) {
            next.addEventListener( 'click', function () {
                row.scrollBy( { left: step(), behavior: 'smooth' } );
            } );
        }

        row.addEventListener( 'scroll', updateDisabled, { passive: true } );
        window.addEventListener( 'resize', updateDisabled );
        updateDisabled();
    } );
})();

// =============================================================================
// End of main.js
// Add any additional scripts above this line.
// =============================================================================